/** Parse create/modify prompts for the card carousel. */

import { CARD_ENTRANCES, CARD_LAYOUTS, clampCarouselParams } from './template';

const STOP = new Set(['a', 'an', 'the', 'about', 'for', 'with', 'and', 'of', 'to', 'in', 'on', 'item', 'items', 'card', 'cards', 'carousel', 'animated', 'create', 'show', 'make', 'list']);

export function extractListedItems(text) {
  const raw = String(text || '');
  const numbered = [...raw.matchAll(/(?:^|\n)\s*(?:\d+[.)]|[-•])\s+([^\n]+)/g)]
    .map((m) => String(m[1] || '').trim())
    .filter(Boolean);
  if (numbered.length >= 2) return numbered.slice(0, 20);
  return [];
}

export function defaultItemsForTopic(topic, count) {
  const n = Math.max(1, Math.min(12, Number(count) || 4));
  const seed = String(topic || 'Item').replace(/\s+/g, ' ').trim() || 'Item';
  const skincare = /skincare|skin care|beauty|serum|sunscreen|moisturizer/i.test(seed);
  if (skincare) {
    const pack = ['Cleanser', 'Moisturizer', 'Serum', 'Sunscreen', 'Lip Balm', 'Face Mask', 'Toner', 'Eye Cream'];
    return pack.slice(0, n);
  }
  return Array.from({ length: n }, (_, i) => `${seed} ${i + 1}`);
}

export function parseCarouselCreatePrompt(text) {
  const raw = String(text || '').trim();
  const listed = extractListedItems(raw);
  const countMatch = raw.match(/(\d+)\s*[- ]?(?:item|card|product|step|place)?s?/i);
  let count = listed.length || (countMatch ? Number(countMatch[1]) : 4);
  count = Math.max(1, Math.min(12, count));
  let topic = raw
    .replace(/\b(create|make|show|build|add|an|a|animated|cards?|carousel|listicle|items?|about)\b/gi, ' ')
    .replace(/\d+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!topic) topic = 'list';
  const items = listed.length >= 2 ? listed.slice(0, count) : defaultItemsForTopic(topic, count);
  let layout = 'horizontal';
  if (/vertical|stack/i.test(raw)) layout = 'vertical';
  else if (/grid/i.test(raw)) layout = 'grid';
  else if (/offset|scatter/i.test(raw)) layout = 'offset';
  else if (/center|hero|focus/i.test(raw)) layout = 'center';
  else if (count >= 5) layout = 'horizontal';
  else if (count === 4) layout = 'grid';
  return {
    topic,
    items,
    params: clampCarouselParams({
      layout,
      animationIn: /energetic|bounce|pop/i.test(raw) ? 'popIn' : 'popIn',
      staggerDelay: 0.25,
    }),
  };
}

export function applyCarouselEditPrompt(params, text) {
  const raw = String(text || '').toLowerCase();
  const next = clampCarouselParams(params);
  if (/faster|quicker|snappier/.test(raw)) {
    next.staggerDelay = Math.max(0.08, next.staggerDelay * 0.55);
    next.animationDuration = Math.max(0.18, next.animationDuration * 0.75);
  }
  if (/slower|calmer|gentle/.test(raw)) {
    next.staggerDelay = Math.min(0.7, next.staggerDelay * 1.5);
    next.animationDuration = Math.min(0.8, next.animationDuration * 1.25);
  }
  if (/bigger|larger|huge/.test(raw)) next.scale = Math.min(1.55, next.scale * 1.22);
  if (/smaller|tiny/.test(raw)) next.scale = Math.max(0.6, next.scale * 0.82);
  if (/energetic|punchy|bounc/.test(raw)) {
    next.animationIn = 'bounceIn';
    next.staggerDelay = Math.min(next.staggerDelay, 0.18);
  }
  if (/premium/.test(raw)) next.styleId = 'premium';
  if (/magazine/.test(raw)) next.styleId = 'magazine';
  if (/only\s+(\d+)/.test(raw)) {
    next.visibleCount = Math.max(1, Number(raw.match(/only\s+(\d+)/)[1]));
  }
  CARD_LAYOUTS.forEach((row) => {
    if (raw.includes(row.id) || raw.includes(row.label.toLowerCase())) next.layout = row.id;
  });
  CARD_ENTRANCES.forEach((row) => {
    if (raw.includes(row.label.toLowerCase()) || raw.includes(row.id.toLowerCase())) {
      next.animationIn = row.id;
    }
  });
  if (/different direction|alternat/.test(raw)) next.alternatingDirection = true;
  if (/slide up/.test(raw)) next.animationIn = 'slideUp';
  if (/no animation|static/.test(raw)) next.animationIn = 'none';
  return next;
}

export function topicTokens(topic) {
  return String(topic || '')
    .toLowerCase()
    .split(/\W+/)
    .filter((w) => w.length > 2 && !STOP.has(w));
}
